// Imports ------------------------------------------------------------------->

import {Service, service, inject} from "./di";
import {ConfigService, RuntimeConfig} from "../services/ConfigService";
import {MIN_BROWSER_MAP} from "../utils/browsers";
import {AnalyticsService} from "../services/AnalyticsService";
import {PageStatus} from "../definitions/CommonDefinitions";
import {i18n} from "../i18n/I18n";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->
@service("errorService")
export class ErrorService extends Service {

	@inject config: ConfigService;
	@inject analytics: AnalyticsService;

	private _errorCount = 0;
	private log = !this.config.isProduction();

	get errorCount() {
		return this._errorCount;
	}

	siteLoaded() {
		window.addEventListener("error", this.reportError.bind(this) as any);
		window.addEventListener("unhandledrejection", this.reportError.bind(this) as any);
	}

	reportError(err: Error) {
		if (this.log) {
			console.error("Page could not render:", err);
		}

		if ((window as any).Raven) {
			Raven.captureException(err as any, {
				tags: {
					// Add a tag for the runtime (distribution site vs native)
					runtime: this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ? "distribution" : "default",
				},
				// If error occurs before initial page is loaded, it's considered a "fatal" error because
				// the page will not be rendered
				level: flipp.status !== PageStatus.Loading ? "error" : "fatal"
			});
		}
		this._showErrorPage();
		this._errorCount++;
	}

	private _showErrorPage = () => {
		if (flipp.status === PageStatus.Loading) {

			const isOldBrowser = bowser.isUnsupportedBrowser(MIN_BROWSER_MAP);

			const e1 = document.querySelector(".error-msg-title");
			const e2 = document.querySelector(".error-msg-subtitle");
			const e3 = document.querySelector(".error-msg-cta");
			const linksArea = document.querySelector(".links-content");
			const browserImages = document.querySelectorAll("#error-page .browser-img");

			if (e1) {
				e1.innerHTML = isOldBrowser ? i18n("old_browser_warning_msg") :
					i18n("runtime_error_title");
			}

			if (e2) {
				e2.innerHTML = isOldBrowser ? i18n(`${this.config.RUNTIME === RuntimeConfig.PG ? "pg_" : ""}old_browser_latest_tech_msg`) : "";
			}

			if (e3) {
				e3.innerHTML = isOldBrowser ?	i18n("old_browser_cta") :
					i18n(`${this.config.RUNTIME === RuntimeConfig.PG ? "pg_" : ""}runtime_error_msg`);
			}

			// FIXME: this is duplicated in site.html.hbs.
			if (isOldBrowser) {
				if (linksArea) {
					(linksArea as HTMLElement).style.display = "flex";
				}
				if (browserImages) {
					for (let i = 0; i < browserImages.length; i++) {
						const item = browserImages.item(i) as HTMLImageElement;
						if (item.classList.contains("chrome")) {
							item.src = `https://f.wishabi.net/creative/Distribution/images/browsers/google-chrome.png`;
						} else if (item.classList.contains("ff")) {
							item.src = `https://f.wishabi.net/creative/Distribution/images/browsers/mozilla-firefox.png`;
						} else if (item.classList.contains("edge")) {
							item.src = `https://f.wishabi.net/creative/Distribution/images/browsers/ms-edge.png`;
						} else if (item.classList.contains("safari")) {
							item.src = `https://f.wishabi.net/creative/Distribution/images/browsers/apple-safari.png`;
						}
					}
				}
			} else if (linksArea) {
				(linksArea as HTMLElement).style.display = "none";
			}
			const logoImg = document.querySelector("#error-page .flipp-logo-wrapper > img") as HTMLImageElement;
			if (logoImg) {
				logoImg.src = `https://f.wishabi.net/creative/Distribution/images/flipp-logo.png`;
			}
			const iceCream = document.querySelector("#error-page .sad-cream") as HTMLImageElement;
			if (iceCream) {
				iceCream.src = `https://f.wishabi.net/creative/Distribution/images/sad-ice-cream-new.png`;
			}

			// We caught an error during the very first initialization.
			// Most likely this happened due to an older unsupported browser.
			// Display a "old-browser" page to the user.
			// NOTE: We do NOT display that if the error has occurred AFTER the
			// initial load.
			// ----------------------------------------------------------------------
			document.body.classList.remove("loading");
			const errorPageSection = document.getElementById("error-page");
			if (errorPageSection) {
				errorPageSection.style.display = "block";
				errorPageSection.setAttribute("data-bundle", isOldBrowser ? "browser" : "error");
			}
			document.getElementById("app").style.display = "none";

			flipp.status = PageStatus.Error;
			if (this.analytics.isInitialized && this.errorCount === 0) {
				// Report the error to GA, but only on the first error (there are some errors that "cascade"
				// through the try/catch handlers so we dont want to report it multiple times).
				if (isOldBrowser) {
					this.analytics.trackUnsupportedBrowserErrorPage();
				} else {
					this.analytics.trackFatalRuntimeErrorPage();
				}
			}
		}
	}
}
